/**
 * Environmental Settings — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on the
 * EnvironmentalSettingsPage (Temperature, Humidity, Battery tabs).
 *
 * Data table: core_environmental_monitoring_control (PK: control_id)
 *
 * environment_type_id:  1 = Temperature, 2 = Humidity, 3 = Battery
 * environmental_alarm_type_id:  1 = Global, 2 = Occupied, 3 = Unoccupied, 4 = Custom
 *
 * Threshold values stored as-is (°F for temp, % for humidity, V for battery).
 * test_after stored in seconds; UI displays as minutes (÷60).
 * device_ids stored as comma-separated string of device IDs.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/** Fetch environment control records by type for a campus. */
export function fetchEnvironmentControls(campusId, envTypeId) {
    return apiFetch({
        procedure: 'sp_dev_getEnvironmentControls',
        reqType: 'stored',
        payload: {
            campus_id: campusId,
            environment_type_filter: envTypeId,
        },
    });
}

/** Fetch Inovonics devices filtered by device category for a campus. */
export function fetchEnvironmentDevices(campusId, categoryFilter) {
    return apiFetch({
        procedure: 'sp_dev_getInovonicsDevices',
        reqType: 'stored',
        payload: {
            device_filter: '',
            device_category_filter: categoryFilter,
            campus_id: campusId,
        },
    });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/** Add a new environment control record. */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section D): Super Admin+; only these columns, and only rows of the request's campus. device_ids must be a comma-separated id list. */
export function addEnvironmentControl(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'env_control_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit an existing environment control record. */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section D): Super Admin+; only these columns, and only rows of the request's campus. The control's type is not editable. */
export function editEnvironmentControl(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'env_control_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Delete a single environment control record. */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section D): Super Admin+; only these columns, and only rows of the request's campus. */
export function deleteEnvironmentControl(controlId, userId, { campusId, clientId }) {
    return apiTransaction({
        transType: 'env_control_delete',
        payload: [{ control_id: controlId, modified_by: userId }],
        campusId,
        userId,
        clientId,
    });
}

/** Delete multiple environment control records sequentially. */
export async function deleteEnvironmentControls(controlIds, userId, meta) {
    for (const id of controlIds) {
        const [ok, err] = await deleteEnvironmentControl(id, userId, meta);
        if (!ok) return [false, err];
    }
    return [true];
}

// ---------------------------------------------------------------------------
// CREATE DEFAULTS
// ---------------------------------------------------------------------------

/**
 * Create default control records for a given environment type.
 *
 * Temperature (1): Occupied Areas + Unoccupied Areas
 * Humidity (2):    Occupied Areas + Unoccupied Areas
 * Battery (3):     Global Settings
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section D): Super Admin+; only these columns, and only rows of the request's campus. */
export function createDefaultControls(envTypeId, { campusId, userId, clientId }) {
    let payload;

    if (envTypeId === 1) {
        // Temperature defaults
        payload = [
            {
                description: 'Occupied Areas',
                low_alarm: 65,
                high_alarm: 78,
                environmental_alarm_type_id: 2,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 1,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
            {
                description: 'Unoccupied Areas',
                low_alarm: 55,
                high_alarm: 90,
                environmental_alarm_type_id: 3,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 1,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
        ];
    } else if (envTypeId === 2) {
        // Humidity defaults
        payload = [
            {
                description: 'Occupied Areas',
                low_alarm: 65,
                high_alarm: 78,
                environmental_alarm_type_id: 2,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 2,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
            {
                description: 'Unoccupied Areas',
                low_alarm: 55,
                high_alarm: 90,
                environmental_alarm_type_id: 3,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 2,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
        ];
    } else {
        // Battery default
        payload = [
            {
                description: 'Global Settings',
                low_alarm: 2.4,
                environmental_alarm_type_id: 1,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 3,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
        ];
    }

    return apiTransaction({
        transType: 'env_control_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}
